import { Box, Skeleton } from "@mui/material";
import { type FC, useState } from "react";
import { CONTROL_HEIGHT } from "@/theme/tokens";
import { type RandomRange, randomBetween, randomCount } from "./skeletonVariants";

/** Days in the week the timeline spans, one column each. */
const DAY_COUNT = 7;
/** The chance a day in a row holds a shift block. */
const SHIFT_CHANCE = 0.6;
/** How the timeline varies per mount: its row count, each row label's width and each shift block's width (% of the cell). */
const TIMELINE_RANGES = {
	rows: [6, 9],
	labelWidth: [40, 70],
	shiftWidth: [60, 100],
} as const satisfies Record<string, RandomRange>;

/** A staffing grid's shape: a row of day headers, then rows (a person or a unit) with shift blocks at random places. */
export const TimelineSkeletonVariant: FC = () => {
	const [rows] = useState(() =>
		Array.from({ length: randomCount(TIMELINE_RANGES.rows) }, () => ({
			labelWidth: randomBetween(TIMELINE_RANGES.labelWidth),
			shifts: Array.from({ length: DAY_COUNT }, () => (Math.random() < SHIFT_CHANCE ? randomBetween(TIMELINE_RANGES.shiftWidth) : null)),
		})),
	);

	return (
		<Box sx={{ display: "grid", gridTemplateColumns: `minmax(0, 1.5fr) repeat(${DAY_COUNT}, minmax(0, 1fr))`, gap: 1 }}>
			<Box />
			{Array.from({ length: DAY_COUNT }, (_, day) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: placeholder day headers are a fixed week; nothing reorders them.
				<Skeleton key={day} variant="text" width="50%" sx={{ justifySelf: "center" }} />
			))}
			{rows.map((row, rowIndex) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: placeholder rows are a fixed stack of bars; nothing reorders them.
				<Box key={rowIndex} sx={{ display: "contents" }}>
					<Skeleton variant="text" width={`${row.labelWidth}%`} />
					{row.shifts.map((shiftWidth, day) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: placeholder day cells are a fixed week; nothing reorders them.
						<Box key={day} sx={{ display: "flex", alignItems: "center", py: 0.5 }}>
							{shiftWidth !== null && <Skeleton variant="rounded" sx={{ width: `${shiftWidth}%`, height: CONTROL_HEIGHT.COMPACT, borderRadius: 1 }} />}
						</Box>
					))}
				</Box>
			))}
		</Box>
	);
};
